import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { X, Plus, Loader2, Search, Star, Clock, Calendar, Users } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import { skillsService } from "@/services/skillsService";

interface Skill {
  id?: string;
  skill_name: string;
  expertise_level: string;
  willing_to_mentor: boolean;
  seeking_mentorship: boolean;
  visibility?: 'private' | 'manager' | 'team';
}

interface UpdateSkillsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EXPERTISE_LEVELS = [
  { value: "beginner", label: "Beginner", description: "Just starting to learn" },
  { value: "intermediate", label: "Intermediate", description: "Can work independently" },
  { value: "advanced", label: "Advanced", description: "Deep knowledge & experience" },
  { value: "expert", label: "Expert", description: "Can teach & lead others" },
];

const POPULAR_SKILLS = [
  "React", "TypeScript", "Python", "Project Management", "Data Analysis",
  "UI/UX Design", "Communication", "Leadership", "Strategic Planning",
  "Machine Learning", "DevOps", "Product Management", "Sales", "Marketing"
];

export default function UpdateSkillsModal({ isOpen, onClose }: UpdateSkillsModalProps) {
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Skills state
  const [skills, setSkills] = useState<Skill[]>([]);
  const [newSkillName, setNewSkillName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Availability state
  const [isAvailable, setIsAvailable] = useState(true);
  const [availabilityHours, setAvailabilityHours] = useState({ start: "09:00", end: "17:00" });
  const [maxRequestsPerWeek, setMaxRequestsPerWeek] = useState(5);
  const [preferredMethod, setPreferredMethod] = useState("chat");
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    if (isOpen && user?.id) {
      loadUserSkills();
      loadAvailability();
    }
  }, [isOpen, user?.id]);

  const loadUserSkills = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      // apiService returns data directly (response.data.data), not wrapped
      const skillsData = await skillsService.getMySkills();

      if (Array.isArray(skillsData)) {
        setSkills(skillsData.map((skill: any) => ({
          id: skill.id,
          skill_name: skill.skillName || skill.skill_name,
          expertise_level: skill.expertiseLevel || skill.expertise_level || 'intermediate',
          willing_to_mentor: skill.willingToMentor !== false,
          seeking_mentorship: skill.seekingMentorship || false,
          visibility: skill.visibility || 'team',
        })));
      } else {
        setSkills([]);
      }
    } catch (error) {
      console.error('Error loading skills:', error);
      toast.error('Failed to load your skills');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailability = async () => {
    if (!user?.id) return;

    try {
      // apiService returns data directly (response.data.data), not wrapped
      const data = await skillsService.getAvailability();

      if (data) {
        // Backend uses isAvailable, maxRequestsPerWeek, preferredConnectionMethod, availabilityHours
        setIsAvailable(data.isAvailable !== false);
        setMaxRequestsPerWeek(data.maxRequestsPerWeek || 5);
        setPreferredMethod(data.preferredConnectionMethod || 'chat');
        setStatusMessage(""); // Backend doesn't have statusMessage
        
        // Parse availabilityHours JSON if available
        if (data.availabilityHours) {
          try {
            const hours = typeof data.availabilityHours === 'string' 
              ? JSON.parse(data.availabilityHours) 
              : data.availabilityHours;
            if (hours.start && hours.end) {
              setAvailabilityHours({ start: hours.start, end: hours.end });
            }
          } catch (e) {
            console.error('Error parsing availability hours:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error loading availability:', error);
    }
  };

  const addSkill = async (skillName: string) => {
    if (!user?.id || !skillName.trim()) return;

    try {
      const data = await skillsService.registerMentorSkill({
        skillName: skillName.trim(),
        expertiseLevel: 'intermediate',
        willingToMentor: true,
        seekingMentorship: false,
        visibility: 'team',
      });

      if (data && data.id) {
        const newSkill: Skill = {
          id: data.id,
          skill_name: data.skillName || skillName.trim(),
          expertise_level: data.expertiseLevel || 'intermediate',
          willing_to_mentor: data.willingToMentor !== false,
          seeking_mentorship: data.seekingMentorship || false,
          visibility: data.visibility || 'team',
        };
        
        setSkills([...skills, newSkill]);
        setNewSkillName("");
        toast.success(`Added ${skillName} to your skills`);
      } else {
        throw new Error('Failed to add skill');
      }
    } catch (error) {
      console.error('Error adding skill:', error);
      toast.error('Failed to add skill');
    }
  };

  const updateSkill = async (index: number, updates: Partial<Skill>) => {
    const skill = skills[index];
    if (!skill.id) return;

    try {
      // Map to API format with correct field names
      const apiUpdates: any = {};
      if (updates.expertise_level) {
        apiUpdates.expertiseLevel = updates.expertise_level;
      }
      if (updates.willing_to_mentor !== undefined) {
        apiUpdates.willingToMentor = updates.willing_to_mentor;
      }
      if (updates.seeking_mentorship !== undefined) {
        apiUpdates.seekingMentorship = updates.seeking_mentorship;
      }
      if (updates.visibility) {
        apiUpdates.visibility = updates.visibility;
      }

      await skillsService.updateMentorSkill(skill.id, apiUpdates);

      // Update local state
      const updatedSkills = [...skills];
      updatedSkills[index] = { ...skill, ...updates };
      setSkills(updatedSkills);
    } catch (error) {
      console.error('Error updating skill:', error);
      toast.error('Failed to update skill');
    }
  };

  const removeSkill = async (index: number) => {
    const skill = skills[index];
    if (!skill.id) return;

    try {
      await skillsService.unregisterMentorSkill(skill.id);

      setSkills(skills.filter((_, i) => i !== index));
      toast.success(`Removed ${skill.skill_name}`);
    } catch (error) {
      console.error('Error removing skill:', error);
      toast.error('Failed to remove skill');
    }
  };

  const saveAvailability = async () => {
    if (!user?.id) return;

    setIsSaving(true);
    try {
      // apiService returns data directly, throws error on failure
      await skillsService.updateAvailability({
        available: isAvailable,
        hoursPerWeek: maxRequestsPerWeek,
        preferredDays: [],
        preferredTimes: [availabilityHours.start, availabilityHours.end],
        preferredMethod: preferredMethod,
      });

      toast.success('Availability settings saved');
      onClose();
    } catch (error) {
      console.error('Error saving availability:', error);
      toast.error('Failed to save availability settings');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredPopularSkills = POPULAR_SKILLS.filter(
    skill => 
      !skills.some(s => s.skill_name.toLowerCase() === skill.toLowerCase()) &&
      skill.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Manage Your Skills & Availability</DialogTitle>
          <DialogDescription>
            Update your skills and set your availability for helping teammates
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="skills" className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="skills">Skills</TabsTrigger>
            <TabsTrigger value="availability">Availability</TabsTrigger>
          </TabsList>

          <TabsContent value="skills" className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Add New Skill */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Add New Skill</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter a skill..."
                    value={newSkillName}
                    onChange={(e) => setNewSkillName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addSkill(newSkillName)}
                  />
                  <Button onClick={() => addSkill(newSkillName)} disabled={!newSkillName.trim()}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>

                {/* Popular Skills */}
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search popular skills..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {filteredPopularSkills.slice(0, 8).map((skill) => (
                      <Badge
                        key={skill}
                        variant="outline"
                        className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                        onClick={() => addSkill(skill)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Current Skills */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Your Skills</CardTitle>
                <CardDescription>
                  Manage your expertise level and mentorship preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : skills.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">
                    No skills added yet. Add some skills above!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {skills.map((skill, index) => (
                      <div key={skill.id || index} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-medium text-base">{skill.skill_name}</h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSkill(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <Label className="text-sm">Expertise Level</Label>
                            <Select
                              value={skill.expertise_level}
                              onValueChange={(value) => updateSkill(index, { expertise_level: value })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {EXPERTISE_LEVELS.map((level) => (
                                  <SelectItem key={level.value} value={level.value}>
                                    <div>
                                      <div className="font-medium">{level.label}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {level.description}
                                      </div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex gap-4">
                            <div className="flex items-center space-x-2">
                              <Switch
                                id={`mentor-${index}`}
                                checked={skill.willing_to_mentor}
                                onCheckedChange={(checked) => 
                                  updateSkill(index, { willing_to_mentor: checked })
                                }
                              />
                              <Label htmlFor={`mentor-${index}`} className="text-sm">
                                Willing to mentor
                              </Label>
                            </div>

                            <div className="flex items-center space-x-2">
                              <Switch
                                id={`learn-${index}`}
                                checked={skill.seeking_mentorship}
                                onCheckedChange={(checked) => 
                                  updateSkill(index, { seeking_mentorship: checked })
                                }
                              />
                              <Label htmlFor={`learn-${index}`} className="text-sm">
                                Want to learn more
                              </Label>
                            </div>
                          </div>

                          <div className="mt-3">
                            <Label className="text-sm">Visibility</Label>
                            <Select
                              value={skill.visibility || 'team'}
                              onValueChange={(value: 'private' | 'manager' | 'team') => 
                                updateSkill(index, { visibility: value })
                              }
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="private">
                                  <div>
                                    <div className="font-medium">Private</div>
                                    <div className="text-xs text-muted-foreground">
                                      Only you can see this skill
                                    </div>
                                  </div>
                                </SelectItem>
                                <SelectItem value="manager">
                                  <div>
                                    <div className="font-medium">Manager Only</div>
                                    <div className="text-xs text-muted-foreground">
                                      You and your manager can see this
                                    </div>
                                  </div>
                                </SelectItem>
                                <SelectItem value="team">
                                  <div>
                                    <div className="font-medium">Team Wide</div>
                                    <div className="text-xs text-muted-foreground">
                                      Everyone on your team can see this
                                    </div>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="availability" className="space-y-4 max-h-[60vh] overflow-y-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Availability Settings</CardTitle>
                <CardDescription>
                  Control when and how teammates can request your help
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Available Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="available">Available for help requests</Label>
                    <p className="text-sm text-muted-foreground">
                      Teammates can see you're available to help
                    </p>
                  </div>
                  <Switch
                    id="available"
                    checked={isAvailable}
                    onCheckedChange={setIsAvailable}
                  />
                </div>

                {/* Working Hours */}
                <div className="space-y-2">
                  <Label>Working Hours</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <Input
                      type="time"
                      value={availabilityHours.start}
                      onChange={(e) => setAvailabilityHours({ ...availabilityHours, start: e.target.value })}
                      className="w-24"
                    />
                    <span>to</span>
                    <Input
                      type="time"
                      value={availabilityHours.end}
                      onChange={(e) => setAvailabilityHours({ ...availabilityHours, end: e.target.value })}
                      className="w-24"
                    />
                  </div>
                </div>

                {/* Max Requests */}
                <div className="space-y-2">
                  <Label>Maximum requests per week</Label>
                  <Select
                    value={maxRequestsPerWeek.toString()}
                    onValueChange={(value) => setMaxRequestsPerWeek(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 3, 5, 10, 20].map((num) => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} requests
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preferred Method */}
                <div className="space-y-2">
                  <Label>Preferred connection method</Label>
                  <Select value={preferredMethod} onValueChange={setPreferredMethod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chat">Chat message</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="meeting">Video meeting</SelectItem>
                      <SelectItem value="async">Async (comments/docs)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Message */}
                <div className="space-y-2">
                  <Label>Status message (optional)</Label>
                  <Textarea
                    placeholder="e.g., Limited availability this week due to deadlines"
                    value={statusMessage}
                    onChange={(e) => setStatusMessage(e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={saveAvailability} disabled={isSaving}>
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Settings
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}